import { useLogger } from '@/composables/useLogger.ts'
import { mercureSubscribe, type MercureSubscribeResponse } from '@/api'
import { useTitropEvents } from '@/composables/useTitropEvents.ts'

export type MercureCallback<T = unknown> = (data: T) => void
export type MercureUnsubscriber = () => void

const logger = useLogger('Mercure')
const eventRegistry = new Map<string, MercureCallback[]>()
const eventSource = ref<EventSource>()
const mercureMeta = ref<MercureSubscribeResponse>()
const autoReconnect = useCountdown(30 * 60 * 1000, {
  async onComplete() {
    mercureMeta.value = (await mercureSubscribe()).data
    console.log(`complete - new token exp: ${mercureMeta.value?.token_expiration} -> ${mercureMeta.value?.token}`)
    autoReconnect.start()
  }
})

export interface MercureMessage {
  '@context': string
  '@type'?: string
  '@id'?: string
}

//export const useMercure = new MercureClient()
export function useMercure() {
  async function connect() {
    disconnect()
    mercureMeta.value = (await mercureSubscribe()).data

    if (!mercureMeta.value?.url || !mercureMeta.value.token) {
      logger.error("Couldn't get mercure meta")
      setTimeout(() => void connect(), 10000)
      return
    }

    const url = new URL(mercureMeta.value.url)
    url.searchParams.set('authorization', mercureMeta.value.token)
    eventSource.value = new EventSource(url, { withCredentials: false })
    eventSource.value.onopen = onOpen
    eventSource.value.onerror = onError
    eventSource.value.onmessage = onMessage
  }

  function disconnect() {
    if (eventSource.value) {
      autoReconnect.stop()
      eventSource.value.close()
      eventSource.value = undefined
      mercureMeta.value = undefined
      logger.info('stream disconnected')
      useTitropEvents().onMercureDisconnected.emit()
    }
  }

  function onOpen() {
    logger.debug('stream connected')
    useTitropEvents().onMercureConnected.emit()
    autoReconnect.start()
  }

  function onError(ev: Event) {
    autoReconnect.stop()
    const delayedReconnect = () => setTimeout(() => void connect(), 10000)
    const instantReconnect = () => void connect()

    if (!mercureMeta.value?.url || !mercureMeta.value.token || !mercureMeta.value.token_expiration) {
      logger.error('stream failed to connect', ev)
      return
    }

    if (mercureMeta.value.token_expiration < Date.now() / 1000) {
      logger.error('token expired - reconnecting', ev)
      return void connect()
    }

    const mercureUrl = new URL(mercureMeta.value.url)
    const checkUrl = new URL(`${mercureUrl.origin}${mercureUrl.pathname}/health`)

    fetch(checkUrl.toString(), { method: 'GET', cache: 'no-cache' })
      .then((response: Response) => {
        logger.error('Mercure service seems down', response)
        delayedReconnect()
      })
      .catch((err) => {
        logger.error(err)
        instantReconnect()
      })
  }

  function onMessage(event: MessageEvent) {
    logger.trace('message received', event.data)
    try {
      const data = JSON.parse(event.data)
      const evtType = data['@type']?.toLowerCase()

      if (!evtType && data['@id']) {
        // DELETE notice, TODO
        return
      }

      if (evtType) dispatch(evtType, data)
      dispatch('*', data)

      if (!eventRegistry.has(evtType) && !eventRegistry.has('*')) {
        logger.debug('no global or specific handler for event', data)
      }
    } catch (e) {
      logger.error('unexpected error occurred', e)
    }
  }

  function dispatch(type: string, data: unknown) {
    if (!eventRegistry.has(type)) return
    for (const cb of eventRegistry.get(type) ?? []) {
      if (!cb) continue
      try {
        logger.trace('calling handler', cb)
        cb(data)
      } catch (e) {
        logger.warn('handler threw error', e, { callback: cb, data })
      }
    }
  }

  function subscribe<T = MercureMessage>(type: string, cb: MercureCallback<T>): MercureUnsubscriber {
    const key = type.toLowerCase()
    if (!eventRegistry.has(key)) eventRegistry.set(key, [])
    const list = eventRegistry.get(key) as MercureCallback<never>[]
    list.push(cb as MercureCallback)

    return () => {
      const idx = list.indexOf(cb as MercureCallback)
      if (idx !== -1) list.splice(idx, 1)
    }
  }

  //
  // onUnmounted(() => {
  //   disconnect()
  // })

  return {
    connect,
    disconnect,
    subscribe
  }
}
